import { useCallback, useEffect, useRef, useState } from "react";
export interface RecordingController {
  supported: boolean;
  recording: boolean;
  duration: number;
  url: string | null;
  mimeType: string;
  error: string | null;
  start: (deviceId?: string) => Promise<void>;
  stop: () => void;
  clear: () => void;
  download: () => void;
}
export function useRecorder(): RecordingController {
  const supported =
    typeof MediaRecorder !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia;
  const [recording, setRecording] = useState(false),
    [duration, setDuration] = useState(0),
    [url, setUrl] = useState<string | null>(null),
    [mimeType, setMimeType] = useState("audio/webm"),
    [error, setError] = useState<string | null>(null);
  const recorder = useRef<MediaRecorder | null>(null),
    stream = useRef<MediaStream | null>(null),
    urlRef = useRef<string | null>(null),
    started = useRef(0),
    permissionGeneration = useRef(0),
    takeGeneration = useRef(0),
    mounted = useRef(true);
  const clear = useCallback(() => {
    takeGeneration.current++;
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
    setUrl(null);
  }, []);
  const stop = useCallback(() => {
    permissionGeneration.current++;
    if (recorder.current?.state === "recording") recorder.current.stop();
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    if (mounted.current) setRecording(false);
  }, []);
  const start = useCallback(
    async (deviceId?: string) => {
      if (!supported) {
        setError(
          "Audio recording needs HTTPS or localhost and a compatible browser.",
        );
        return;
      }
      stop();
      clear();
      setError(null);
      const request = permissionGeneration.current,
        take = takeGeneration.current;
      try {
        const input = await navigator.mediaDevices.getUserMedia({
          audio: deviceId ? { deviceId: { exact: deviceId } } : true,
          video: false,
        });
        if (request !== permissionGeneration.current || !mounted.current) {
          input.getTracks().forEach((t) => t.stop());
          return;
        }
        stream.current = input;
        const mime =
          ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4"].find(
            (t) => MediaRecorder.isTypeSupported(t),
          ) || "";
        const next = new MediaRecorder(
          input,
          mime ? { mimeType: mime } : undefined,
        );
        recorder.current = next;
        const chunks: Blob[] = [];
        next.ondataavailable = (e) => {
          if (e.data.size) chunks.push(e.data);
        };
        next.onstop = () => {
          input.getTracks().forEach((t) => t.stop());
          if (
            !mounted.current ||
            take !== takeGeneration.current ||
            !chunks.length
          )
            return;
          const blob = new Blob(chunks, { type: next.mimeType });
          const objectURL = URL.createObjectURL(blob);
          if (urlRef.current) URL.revokeObjectURL(urlRef.current);
          urlRef.current = objectURL;
          setUrl(objectURL);
          setMimeType(next.mimeType);
          setRecording(false);
        };
        next.onerror = () => {
          if (take !== takeGeneration.current || !mounted.current) return;
          setError("Recording failed. Please check your microphone.");
          stop();
        };
        next.start(1000);
        started.current = performance.now();
        setDuration(0);
        setRecording(true);
      } catch (e) {
        if (request !== permissionGeneration.current || !mounted.current)
          return;
        stream.current?.getTracks().forEach((t) => t.stop());
        stream.current = null;
        setError(e instanceof Error ? e.message : "Microphone unavailable.");
        setRecording(false);
      }
    },
    [supported, stop, clear],
  );
  useEffect(() => {
    if (!recording) return;
    const tick = setInterval(() => {
      setDuration((performance.now() - started.current) / 1000);
      if (performance.now() - started.current > 30 * 60 * 1000) stop();
    }, 250);
    return () => clearInterval(tick);
  }, [recording, stop]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      takeGeneration.current++;
      stop();
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [stop]);
  const download = () => {
    if (!urlRef.current) return;
    const a = document.createElement("a");
    a.href = urlRef.current;
    a.download = `cue-take-${new Date().toISOString().replace(/[:.]/g, "-")}.${mimeType.includes("mp4") ? "m4a" : mimeType.includes("ogg") ? "ogg" : "webm"}`;
    a.click();
  };
  return {
    supported,
    recording,
    duration,
    url,
    mimeType,
    error,
    start,
    stop,
    clear,
    download,
  };
}
